import React from 'react'
import { useActiveDomainChangeEffect } from '../../../hooks/use-active-domain-change-effect'
import { parseQueryStringParams } from '@pushly/aqe/lib/utils/query-string'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc'
import { AppService } from '../../../services'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationTemplateService } from '../../../services/notification-template'
import { tryParseInt } from '../../../_utils/try-parse'
import {
    getCustomMacroFields,
    getDomainKeywords,
    getEnabledDeliveryChannels,
    getSecretsMacroFields,
} from '../../../_utils/domain'
import { PageHeader } from '@pushly/aqe/lib/components'
import NotificationTemplatesLoader from './notification-templates-loader'
import NotificationBuilder from '../../../components/notification-builder/notification-builder'
import NotificationBuilderAside from '../../../components/notification-builder/elements/notification-builder-aside'
import NotificationBuilderReach from '../../../components/notification-builder/elements/notification-builder-reach'
import NotificationBuilderPreview from '../../../components/notification-builder/elements/notification-builder-preview'
import {
    buildPreviewOptions,
    buildSubmittedNotificationPackages,
} from '../../../components/notification-builder/helpers'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import NotificationAudienceBuilder from '../../../components/notification-builder/elements/notification-audience-builder'
import NotificationVariantBuilder from '../../../components/notification-builder/elements/notification-variant-builder'
import NotificationBuilderMain from '../../../components/notification-builder/elements/notification-builder-main'
import { useRefEffect } from '../../../hooks/use-ref-effect'
import { useHistory } from 'react-router-dom'
import { TemplateDetailsBuilder } from './template-details-builder'
import { onResponseError403 } from '../../../_utils/on-response-error-403'

export const BuildNamedNotificationTemplate = () => {
    useActiveDomainChangeEffect((activeDomain, appService) => {
        appService.routeWithinDomain('/notifications')
    })

    const { push: updateRoute } = useHistory()
    const qs = parseQueryStringParams()
    const appState: AppState = Container.get(AppState)
    const appSvc: AppService = useService(AppService)
    const notifTemplateSvc: NotificationTemplateService = useService(NotificationTemplateService)

    const domain = appState.currentDomain!
    const requestTemplateId = tryParseInt(qs.get('template_id')?.toString() ?? '') || undefined

    const [keywordOptions] = useRefEffect(() => getDomainKeywords(domain.id), [domain.id])
    const [customMacroOptions] = useRefEffect(() => getCustomMacroFields(domain.id), [domain.id])
    const [secretsMacroOptions] = useRefEffect(() => getSecretsMacroFields(domain.id), [domain.id])

    const goBackToNotifs = () => {
        updateRoute(`/domains/${domain.id}/notifications/templates`)
    }

    const goBackToPreviousNotifsTab = () => {
        appSvc.routeBack()
    }

    const activeDomainChannels = getEnabledDeliveryChannels(domain, true)
    return (
        <>
            <PageHeader title="Create Notification Template" onTitleSet={appSvc.customizeTabTitle} />

            <NotificationTemplatesLoader
                domainId={domain.id}
                templateId={requestTemplateId}
                customErrorHandler={onResponseError403(() => {
                    goBackToNotifs()
                }, true)}
            >
                {(notifs) => {
                    return (
                        <NotificationBuilder
                            mode="create"
                            type="template"
                            domain={domain}
                            notifications={notifs}
                            defaultSelectedNotifId={requestTemplateId}
                        >
                            {(props) => {
                                const {
                                    builder,
                                    dispatchChanges,
                                    validateSubmit,
                                    onChannelChange,
                                    onDetailsChange,
                                    onTestChange,
                                    onAudienceChange,
                                    onDistributionChange,
                                    variants,
                                    onVariantChange,
                                    variantManager,
                                } = props
                                const activeVariant = variants[builder.selectedVariantIdx]
                                const previewOptions = buildPreviewOptions(builder.channels)

                                return (
                                    <>
                                        <NotificationBuilderAside>
                                            <NotificationBuilderReach
                                                domain={domain}
                                                builder={builder}
                                                setBuilder={dispatchChanges}
                                                hideVariantReach={true}
                                                onDistributionChange={onDistributionChange}
                                            />
                                            <NotificationBuilderPreview
                                                level="domain"
                                                levelId={domain.id}
                                                loading={builder.loading}
                                                domain={domain}
                                                source={activeVariant}
                                                options={previewOptions}
                                                hideDevicePreview={true}
                                            />
                                        </NotificationBuilderAside>

                                        <NotificationBuilderMain>
                                            {activeDomainChannels.length > 1 && (
                                                <DeliveryChannelSelector
                                                    type="multiple"
                                                    loading={builder.loading}
                                                    value={builder.channels}
                                                    onChange={onChannelChange}
                                                    visibleChannels={activeDomainChannels}
                                                />
                                            )}

                                            <TemplateDetailsBuilder
                                                loading={builder.loading}
                                                value={builder.details}
                                                onChange={onDetailsChange}
                                            />

                                            <NotificationAudienceBuilder
                                                loading={builder.loading}
                                                domain={domain}
                                                setBuilder={dispatchChanges}
                                                value={activeVariant.getAudience()}
                                                onChange={onAudienceChange}
                                                channels={builder.channels}
                                            />

                                            <NotificationVariantBuilder
                                                key={activeVariant.getId()}
                                                id={builder.selectedVariantIdx}
                                                domain={domain}
                                                builder={builder}
                                                dispatchChanges={dispatchChanges}
                                                variant={activeVariant}
                                                manager={variantManager}
                                                isTemplate={true}
                                                hideDelivery={true}
                                                hideImageMacroToggle={true}
                                                onChange={onVariantChange}
                                                onCancel={goBackToPreviousNotifsTab}
                                                onSubmit={async () => {
                                                    let saved = false
                                                    const builds = buildSubmittedNotificationPackages(
                                                        domain,
                                                        builder,
                                                        appState.flags,
                                                        undefined,
                                                        null,
                                                        true,
                                                    )

                                                    if (builds.length === 1) {
                                                        saved = await notifTemplateSvc.createNamedTemplate(
                                                            domain.id,
                                                            builds[0],
                                                            {
                                                                cancellationKey: 'notif-template-create',
                                                            },
                                                        )
                                                    }

                                                    if (saved) {
                                                        goBackToNotifs()
                                                    }
                                                }}
                                                keywordOptions={keywordOptions.current}
                                                customMacroOptions={customMacroOptions.current}
                                                secretsMacroOptions={secretsMacroOptions.current}
                                            />
                                        </NotificationBuilderMain>
                                    </>
                                )
                            }}
                        </NotificationBuilder>
                    )
                }}
            </NotificationTemplatesLoader>
        </>
    )
}
